import { getTokenChartData } from '../../services/solanatrackerService';

// Define interfaces for TradingView API
interface TradingViewBar {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

interface HistoryMetadata {
    noData?: boolean;
    nextTime?: number;
}

interface LibrarySymbolInfo {
    name: string;
    ticker?: string;
    description?: string;
    type?: string;
    exchange?: string;
    session?: string;
    timezone?: string;
    minmov?: number;
    pricescale?: number;
    has_intraday?: boolean;
    intraday_multipliers?: string[];
    has_seconds?: boolean;
    seconds_multipliers?: string[];
    has_daily?: boolean;
    has_weekly_and_monthly?: boolean;
    supported_resolutions?: string[];
    volume_precision?: number;
    data_status?: string;
    format?: string;
}

interface PeriodParams {
    from: number;
    to: number;
    firstDataRequest?: boolean;
}

interface SubscriberInfo {
    symbolInfo: LibrarySymbolInfo;
    resolution: string;
    timeframe: string;
    tokenMint: string;
    subscriberUID: string;
    onRealtimeCallback: (bar: TradingViewBar) => void;
}

const configurationData = {
    supported_resolutions: ['1S', '5S', '15S', '30S', '1', '5', '15', '30', '60', '120', '240', '360', '480', '720', 'D', 'W', 'M'],
    exchanges: [
        {
            value: 'Solana',
            name: 'Solana',
            desc: 'Solana DEX'
        }
    ],
    symbols_types: [
        {
            name: 'crypto',
            value: 'crypto'
        }
    ]
};

// Maps TradingView resolution to our backend format
const resolutionToTimeframe = (resolution: string): string => {
    switch (resolution) {
        case '1S': return '1s';
        case '5S': return '5s';
        case '15S': return '15s';
        case '30S': return '30s';
        case '1': return '1m';
        case '5': return '5m';
        case '15': return '15m';
        case '30': return '30m';
        case '60': return '1h';
        case '120': return '2h';
        case '240': return '4h';
        case '360': return '6h';
        case '480': return '8h';
        case '720': return '12h';
        case 'D': return '1d';
        case 'W': return '1w';
        case 'M': return '1M';
        default: return '1m';
    }
};

class Datafeed {
    private lastTokenMint: string | null;
    private cachedData: Record<string, { bars: TradingViewBar[], meta: HistoryMetadata }>;
    private subscriber: SubscriberInfo | null;

    constructor() {
        this.lastTokenMint = null;
        this.cachedData = {};
        this.subscriber = null;
    }

    onReady(callback: (configuration: any) => void): void {
        console.log('[Datafeed]: onReady called');

        setTimeout(() => {
            callback(configurationData);
        }, 0);
    }

    resolveSymbol(
        symbolName: string,
        onSymbolResolvedCallback: (symbolInfo: LibrarySymbolInfo) => void,
        onResolveErrorCallback: (reason: string) => void,
        extension?: any
    ): void {
        console.log('[Datafeed]: resolveSymbol called with', symbolName);

        // Store the token mint for later use
        this.lastTokenMint = symbolName;

        // Symbol stub
        const symbolStub: LibrarySymbolInfo = {
            name: symbolName,
            description: `${symbolName}`,
            type: 'crypto',
            exchange: 'Solana',
            session: '24x7',
            timezone: 'Etc/UTC',
            minmov: 1,
            pricescale: 100000000, // Use high precision for crypto
            has_intraday: true,
            intraday_multipliers: ['1S', '5S', '15S', '30S', '1', '5', '15', '30', '60', '120', '240'],
            has_seconds: true,
            seconds_multipliers: ['1S', '5S', '15S', '30S'],
            has_daily: true,
            has_weekly_and_monthly: true,
            supported_resolutions: configurationData.supported_resolutions,
            volume_precision: 8,
            data_status: 'streaming',
            format: 'price'
        };

        setTimeout(() => {
            onSymbolResolvedCallback(symbolStub);
        }, 0);
    }

    getBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        periodParams: PeriodParams,
        onHistoryCallback: (bars: TradingViewBar[], meta: HistoryMetadata) => void,
        onErrorCallback: (error: string) => void
    ): void {
        const { from, to, firstDataRequest } = periodParams;
        console.log('[Datafeed]: getBars called', symbolInfo, resolution, from, to);

        const tokenMint = symbolInfo.name;
        const timeframe = resolutionToTimeframe(resolution);

        // Define a cache key
        const cacheKey = `${tokenMint}_${timeframe}_${from}_${to}`;

        // Check if we have cached data for this request
        if (this.cachedData[cacheKey]) {
            console.log('[Datafeed]: Using cached data for', cacheKey);
            const { bars, meta } = this.cachedData[cacheKey];
            onHistoryCallback(bars, meta);
            return;
        }

        // Fetch data from API
        getTokenChartData(tokenMint, timeframe, from, to)
            .then(result => {
                if (result.status === 'success' && result.data && result.data.length > 0) {
                    // Transform data to TradingView format
                    const bars = result.data.map(bar => ({
                        time: bar.time * 1000, // Convert to ms
                        open: bar.open,
                        high: bar.high,
                        low: bar.low,
                        close: bar.close,
                        volume: bar.volume
                    }));

                    // Create metadata
                    const meta: HistoryMetadata = {
                        noData: false
                    };

                    // Cache the result
                    this.cachedData[cacheKey] = { bars, meta };

                    // Send data to chart
                    onHistoryCallback(bars, meta);
                } else {
                    console.warn('[Datafeed]: No data returned for the request', tokenMint, timeframe, from, to);
                    onHistoryCallback([], { noData: true });
                }
            })
            .catch(error => {
                console.error('[Datafeed]: Error fetching bars', error);
                onErrorCallback(error.toString());
            });
    }

    subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        onRealtimeCallback: (bar: TradingViewBar) => void,
        subscriberUID: string,
        onResetCacheNeededCallback: () => void
    ): void {
        console.log('[Datafeed]: subscribeBars called', symbolInfo, resolution, subscriberUID);
        const tokenMint = symbolInfo.name;
        const timeframe = resolutionToTimeframe(resolution);

        // For real implementation, this would set up WebSocket or other real-time data connection
        // For now, we'll just store the subscriber info
        this.subscriber = {
            symbolInfo,
            resolution,
            timeframe,
            tokenMint,
            subscriberUID,
            onRealtimeCallback
        };
    }

    unsubscribeBars(subscriberUID: string): void {
        console.log('[Datafeed]: unsubscribeBars called', subscriberUID);

        // Clear subscriber info
        if (this.subscriber && this.subscriber.subscriberUID === subscriberUID) {
            this.subscriber = null;
        }
    }

    // This is used for real-time updates
    updateBars(tokenMint: string, latestBar: any): void {
        if (!this.subscriber || this.subscriber.tokenMint !== tokenMint) {
            return;
        }

        // Format the bar according to TradingView's requirements
        const bar: TradingViewBar = {
            time: latestBar.time * 1000, // Convert to ms
            open: latestBar.open,
            high: latestBar.high,
            low: latestBar.low,
            close: latestBar.close,
            volume: latestBar.volume
        };

        // Pass the update to TradingView
        this.subscriber.onRealtimeCallback(bar);
    }

    // Optional method - allows providing chart with additional symbol information
    getMarks(
        symbolInfo: LibrarySymbolInfo,
        from: number,
        to: number,
        onDataCallback: (marks: any[]) => void,
        resolution: string
    ): void {
        console.log('[Datafeed]: getMarks method called');

        // Not implemented for now
        onDataCallback([]);
    }

    // Optional method - allows providing chart with additional price scale information
    calculateHistoryDepth(
        resolution: string,
        resolutionBack: string,
        intervalBack: number
    ): { resolutionBack: string; intervalBack: number } {
        console.log('[Datafeed]: calculateHistoryDepth called', resolution);

        // Default values depending on resolution
        if (resolution === '1S' || resolution === '5S' || resolution === '15S' || resolution === '30S') {
            return {
                resolutionBack: 'D',
                intervalBack: 1 // 1 day
            };
        } else if (resolution === '1' || resolution === '5' || resolution === '15' || resolution === '30') {
            return {
                resolutionBack: 'D',
                intervalBack: 7 // 7 days
            };
        } else if (resolution === '60' || resolution === '120' || resolution === '240') {
            return {
                resolutionBack: 'M',
                intervalBack: 1 // 1 month
            };
        } else {
            return {
                resolutionBack: 'M',
                intervalBack: 3 // 3 months
            };
        }
    }

    // Optional method - allows chart to get server time
    getServerTime(callback: (serverTime: number) => void): void {
        callback(Math.floor(Date.now() / 1000));
    }

    // Clear cached data - useful after price/data changes
    clearCache(): void {
        this.cachedData = {};
    }
}

const datafeedInstance = new Datafeed();

export default {
    onReady: (callback: (configuration: any) => void): void => datafeedInstance.onReady(callback),
    resolveSymbol: (
        symbolName: string,
        onSymbolResolvedCallback: (symbolInfo: LibrarySymbolInfo) => void,
        onResolveErrorCallback: (reason: string) => void,
        extension?: any
    ): void => datafeedInstance.resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback, extension),
    getBars: (
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        periodParams: PeriodParams,
        onHistoryCallback: (bars: TradingViewBar[], meta: HistoryMetadata) => void,
        onErrorCallback: (error: string) => void
    ): void => datafeedInstance.getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback),
    subscribeBars: (
        symbolInfo: LibrarySymbolInfo,
        resolution: string,
        onRealtimeCallback: (bar: TradingViewBar) => void,
        subscriberUID: string,
        onResetCacheNeededCallback: () => void
    ): void => datafeedInstance.subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback),
    unsubscribeBars: (subscriberUID: string): void => datafeedInstance.unsubscribeBars(subscriberUID),
    updateBars: (tokenMint: string, latestBar: any): void => datafeedInstance.updateBars(tokenMint, latestBar),
    clearCache: (): void => datafeedInstance.clearCache()
}; 